import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Info, AlertCircle, Users } from 'lucide-react';

import { Card, Button, Skeleton } from '../ui-v2';
import { getPanelEquipo } from './api';

function iniciales(nombre) {
  const partes = String(nombre || '').trim().split(/\s+/);
  return ((partes[0]?.[0] || '') + (partes[1]?.[0] || '')).toUpperCase() || '?';
}

export default function SoporteEquipo() {
  const [equipo, setEquipo] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const cargar = useCallback(() => {
    setCargando(true); setError(null);
    getPanelEquipo()
      .then((d) => setEquipo(d.equipo || []))
      .catch(() => setError('No se pudo cargar el equipo.'))
      .finally(() => setCargando(false));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <div className="px-6 py-6 min-h-full">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-slate-900">Equipo de soporte</h1>
        <p className="text-sm text-slate-500 mt-1">Personas con permiso para gestionar tickets del módulo de Soporte.</p>
      </div>

      {/* Aviso: el equipo = titulares de soporte.panel.manage */}
      <div className="mb-6 flex items-start gap-2.5 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
        <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
        <p className="text-sm text-blue-900">
          El equipo lo componen las personas con el permiso{' '}
          <code className="text-xs bg-white px-1.5 py-0.5 rounded border border-blue-200">soporte.panel.manage</code>.
          Para añadir o quitar miembros, ve a{' '}
          <Link to="/configuracion/permisos" className="font-medium underline hover:text-blue-700">Configuración → Permisos</Link>.
        </p>
      </div>

      {cargando ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-4">
                <Skeleton width={40} height={40} className="rounded-full" />
                <div className="flex-1"><Skeleton className="h-3 w-28 mb-2" /><Skeleton className="h-3 w-40" /></div>
              </div>
              <Skeleton className="h-8 w-full" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="py-16 text-center">
          <AlertCircle className="w-10 h-10 mx-auto text-red-400 mb-3" />
          <p className="text-sm font-medium text-slate-700">No se pudo cargar el equipo</p>
          <p className="text-sm text-slate-500 mt-1">Inténtalo de nuevo en unos segundos.</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={cargar}>Reintentar</Button>
        </div>
      ) : equipo.length === 0 ? (
        <div className="py-16 text-center">
          <Users className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-sm font-medium text-slate-700">Aún no hay técnicos de soporte</p>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            Otorga el permiso <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">soporte.panel.manage</code> a las personas del equipo desde{' '}
            <Link to="/configuracion/permisos" className="font-medium underline">Configuración → Permisos</Link>.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {equipo.map((t) => (
            <Card key={t.id} className="p-4 border-slate-200">
              <div className="flex items-center gap-3 mb-4 min-w-0">
                <div className="w-10 h-10 rounded-full bg-primary/10 text-primary grid place-items-center text-sm font-semibold shrink-0">
                  {iniciales(t.nombre)}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{t.nombre || 'Sin nombre'}</div>
                  <div className="text-xs text-slate-500 truncate">{t.email || '—'}</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 pt-3 border-t border-slate-100 text-center">
                <Stat valor={t.stats.abiertos} etiqueta="Abiertos" />
                <Stat valor={t.stats.resueltos_mes} etiqueta="Resueltos/mes" />
                <Stat valor={t.stats.tiempo_medio_horas == null ? '—' : `${t.stats.tiempo_medio_horas}h`} etiqueta="T. medio" />
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ valor, etiqueta }) {
  return (
    <div>
      <div className="text-lg font-semibold text-slate-900">{valor}</div>
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{etiqueta}</div>
    </div>
  );
}
